import { memo, useMemo } from "react";
import Chart from "react-apexcharts";

import "./OperatorRejectReasonsChart.scss";

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const cleanText = (value = "") =>
  String(value).replace(/\s+/g, " ").trim() || "Unknown";

const compactNumber = (value) =>
  new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(safeNumber(value));

function OperatorRejectReasonsChart({
  reasons = [],
  chartKey = "default",
  loading = false,
}) {
  const chartData = useMemo(() => {
    const reasonList = Array.isArray(reasons)
      ? reasons
          .filter(Boolean)
          .map((reason) => ({
            reasonName: cleanText(reason?.reasonName),
            quantity: safeNumber(reason?.quantity),
          }))
          .sort((a, b) => b.quantity - a.quantity)
          .slice(0, 10)
      : [];

    return {
      categories: reasonList.map(
        (reason) => reason.reasonName,
      ),
      quantities: reasonList.map(
        (reason) => reason.quantity,
      ),
    };
  }, [reasons]);

  const series = useMemo(
    () => [
      {
        name: "Rejected Qty",
        data: chartData.quantities,
      },
    ],
    [chartData.quantities],
  );

  const animationKey = useMemo(
    () =>
      [
        chartKey,
        chartData.categories.join("-"),
        chartData.quantities.join("-"),
      ].join("_"),
    [
      chartKey,
      chartData.categories,
      chartData.quantities,
    ],
  );

  const options = useMemo(
    () => ({
      chart: {
        id: `operator-reject-reasons-${chartKey}`,
        type: "bar",

        toolbar: {
          show: false,
        },

        zoom: {
          enabled: false,
        },

        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 1050,

          animateGradually: {
            enabled: true,
            delay: 140,
          },

          dynamicAnimation: {
            enabled: true,
            speed: 650,
          },
        },

        redrawOnParentResize: false,
        redrawOnWindowResize: false,
      },

      colors: ["#ef4444"],

      fill: {
        type: "gradient",

        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.2,
          gradientToColors: ["#dc2626"],
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 0.86,
          stops: [0, 100],
        },
      },

      plotOptions: {
        bar: {
          borderRadius: 7,
          borderRadiusApplication: "end",
          columnWidth: "48%",
          distributed: false,

          dataLabels: {
            position: "top",
          },
        },
      },

      stroke: {
        show: false,
      },

      dataLabels: {
        enabled: true,
        offsetY: -18,

        style: {
          fontSize: "10px",
          fontWeight: 700,
          colors: ["#475569"],
        },

        background: {
          enabled: false,
        },

        formatter: compactNumber,
      },

      states: {
        normal: {
          filter: {
            type: "none",
          },
        },

        hover: {
          filter: {
            type: "lighten",
            value: 0.12,
          },
        },

        active: {
          allowMultipleDataPointsSelection: false,

          filter: {
            type: "darken",
            value: 0.08,
          },
        },
      },

      grid: {
        borderColor: "#f1f5f9",
        strokeDashArray: 4,

        padding: {
          top: 20,
          left: 8,
          right: 12,
          bottom: 4,
        },
      },

      xaxis: {
        categories: chartData.categories,

        labels: {
          rotate: -35,
          rotateAlways: chartData.categories.length > 5,
          trim: true,
          hideOverlappingLabels: true,
          maxHeight: 90,

          style: {
            fontSize: "13px",
            fontWeight: 500,
            colors: "#64748b",
          },

          formatter: (value) => {
            const text = cleanText(value);

            return text.length > 18
              ? `${text.slice(0, 18)}...`
              : text;
          },
        },

        axisBorder: {
          show: false,
        },

        axisTicks: {
          show: false,
        },

        tooltip: {
          enabled: false,
        },
      },

      yaxis: {
        min: 0,
        forceNiceScale: true,

        labels: {
          formatter: compactNumber,

          style: {
            fontSize: "13px",
            colors: "#64748b",
          },
        },
      },

      tooltip: {
        enabled: true,
        shared: false,
        intersect: true,

        x: {
          formatter: (_, context) =>
            chartData.categories[
              context?.dataPointIndex
            ] || "Unknown",
        },

        y: {
          title: {
            formatter: () => "Rejected Qty: ",
          },

          formatter: (value) =>
            safeNumber(value).toLocaleString("en-IN"),
        },
      },

      noData: {
        text: "No rejection reasons found",
        align: "center",
        verticalAlign: "middle",

        style: {
          color: "#94a3b8",
          fontSize: "12px",
        },
      },
    }),
    [chartData.categories, chartKey],
  );

  if (loading) {
    return (
      <div className="operator-reject-reasons-chart operator-reject-reasons-chart--loading">
        <div className="operator-reject-skeleton">
          <div className="operator-reject-skeleton__chart">
            {Array.from({ length: 8 }).map(
              (_, index) => (
                <div
                  key={`reject-skeleton-${index}`}
                  className="operator-reject-skeleton__column"
                >
                  <span
                    className="operator-reject-skeleton__bar"
                    style={{
                      height: `${
                        30 + ((index * 17) % 65)
                      }%`,
                      animationDelay: `${index * 90}ms`,
                    }}
                  />

                  <span className="operator-reject-skeleton__label" />
                </div>
              ),
            )}
          </div>

          <div className="operator-reject-skeleton__loading">
            <span className="operator-reject-spinner" />
            <span>Loading rejection reasons...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!chartData.categories.length) {
    return (
      <div className="operator-reject-reasons-chart operator-reject-reasons-chart--empty">
        <span className="operator-reject-reasons-chart__empty-icon">
          !
        </span>

        <span>No rejection reasons found</span>
      </div>
    );
  }

  return (
    <div
      key={animationKey}
      className="operator-reject-reasons-chart operator-reject-reasons-chart--visible"
    >
      <Chart
        options={options}
        series={series}
        type="bar"
        height={380}
      />
    </div>
  );
}

export default memo(OperatorRejectReasonsChart);